import {
  EventFacetName,
  EventsListResponse,
  EventsListUrlState,
  FinishedOrUnfinishedEvents,
  getEventsListGroqParams,
  getEventsListQuery,
  scopeQueryToSite,
  targetAudiences,
} from "@libry-content/common";
import { omit, sift } from "radash";
import { useEffect } from "react";
import useSWRInfinite from "swr/infinite";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { sanityClient } from "../../utils/sanity/client";
import { useCommonData } from "../layout/CommonDataProvider";
import { ResolvedFacets, resolveFacet } from "../filter/facetUtils";

export type UsePaginatedEventsProps = {
  initialData?: EventsListResponse; // Undefined because of unit-test
  finishedOrUnfinishedEvents: FinishedOrUnfinishedEvents;
  urlState: EventsListUrlState;
};

export const usePaginatedEvents = (props: UsePaginatedEventsProps) => {
  const { site } = useCommonData();
  const { t, ts, lang } = useTranslation();
  const currentPagination = props.urlState?.pagination ?? 1;

  const getKey = (index: number, previousPageData: { endOfResults: boolean } | null) => {
    if (previousPageData?.endOfResults) return null;
    return {
      _key: "events", // a unique key for swr cache, probably not necessary
      index,
      // @ts-expect-error TODO: fix type-error. Cannot have pagination in key as it leads to race conditions
      urlState: omit(props.urlState, ["pagination"]),
    };
  };

  const { data, setSize, isValidating } = useSWRInfinite(
    getKey,
    async (key) => {
      const query = scopeQueryToSite(getEventsListQuery(props.finishedOrUnfinishedEvents));
      const params = {
        siteDomain: site?.domain,
        ...getEventsListGroqParams(key.urlState, key.index + 1, props.finishedOrUnfinishedEvents, lang),
      };

      return await sanityClient.fetch<EventsListResponse>(query, params);
    },
    {
      initialSize: currentPagination,
      //dropper disse revalideringene for å slippe at spinner dukker opp utenom brukerinput
      revalidateIfStale: false,
      revalidateOnFocus: false,
      fallbackData: [props.initialData],
    }
  );

  // Sync swr-pagination with the url
  useEffect(() => {
    setSize(currentPagination);
  }, [currentPagination, setSize]);

  const flattenedEvents = sift(data?.flatMap((data) => data?.events) || []);
  const events = flattenedEvents.length ? flattenedEvents : props.initialData?.events || [];
  const lastResult = data?.[data.length - 1];
  const endOfResults = !!lastResult?.endOfResults;

  const facets = lastResult?.facets ?? props.initialData?.facets;

  const resolvedFacets = {
    libraries: resolveFacet({
      label: t("Bibliotek"),
      facets: facets?.libraries,
      facetToFilter: (library) => ({
        label: ts(library.name)!,
        value: library.slug!,
      }),
      urlState: props.urlState?.libraries,
    }),
    eventTypes: resolveFacet({
      label: t("Kategori"),
      facets: facets?.eventTypes,
      facetToFilter: (eventType) => ({
        label: ts(eventType.label)!,
        value: ts(eventType.label)!,
      }),
      urlState: props.urlState?.eventTypes,
    }),
    targetAudiences: resolveFacet({
      label: t("Målgruppe"),
      facets: facets?.targetAudiences,
      facetToFilter: (targetAudienceCode) => ({
        label: ts(targetAudiences[targetAudienceCode])!,
        value: targetAudienceCode!,
      }),
      urlState: props.urlState?.targetAudiences,
    }),
  } satisfies ResolvedEventFacets;

  return { events, facets: resolvedFacets, endOfResults, isValidating };
};

export type ResolvedEventFacets = ResolvedFacets<EventFacetName>;
